import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, dateFr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Truck, SignOut, Receipt, Scales, CheckCircle, Coins, Warning } from "@phosphor-icons/react";

export default function SupplierPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [disputeFor, setDisputeFor] = useState(null);
  const [disputeMsg, setDisputeMsg] = useState("");

  const load = () => api.get("/supplier-portal/statement").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submitDispute = async () => {
    if (!disputeMsg.trim()) { toast.error("Décrivez l'écart constaté."); return; }
    try {
      await api.post(`/supplier-portal/expenses/${disputeFor.id}/dispute`, { message: disputeMsg });
      toast.success("Écart signalé — l'entreprise a été notifiée");
      setDisputeFor(null); setDisputeMsg(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openReceipt = (expenseId, conf) =>
    window.open(`${API}/expenses/${expenseId}/receipt?conf=${conf}&auth=${localStorage.getItem("token")}`, "_blank");

  const cards = data ? [
    { label: "Total facturé", value: money(data.total_billed), icon: Coins, accent: "text-foreground" },
    { label: "Total payé", value: money(data.total_paid), icon: CheckCircle, accent: "text-emerald-700" },
    { label: "Solde dû", value: money(data.balance), icon: Scales, accent: data.balance > 0 ? "text-red-600" : "text-emerald-700" },
  ] : [];

  return (
    <div className="min-h-screen bg-background text-left">
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/innova-fact-dark.png" alt="innova-fact" className="h-8 w-auto" />
            <div className="border-l border-border pl-3">
              <div className="font-heading font-bold leading-none">{data?.company?.business_name || "Portail fournisseur"}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Espace fournisseur</div>
            </div>
          </div>
          <Button data-testid="supplier-portal-logout" variant="outline" size="sm" className="rounded-sm" onClick={async () => { await logout(); navigate("/login"); }}>
            <SignOut size={16} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8 space-y-8" data-testid="supplier-portal-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Bonjour, {user?.name}</h1>
          <p className="text-muted-foreground mt-1">Consultez vos factures et vos confirmations de paiement.</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="border border-border bg-card p-5 rounded-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{c.label}</span>
                <c.icon size={18} className={c.accent} />
              </div>
              <div className={`mt-3 font-heading text-2xl font-bold tabular-nums ${c.accent}`}>{c.value}</div>
            </div>
          ))}
        </div>

        {/* Payment confirmations */}
        <div className="border border-border bg-card rounded-sm overflow-x-auto">
          <div className="px-6 py-4 border-b border-border"><h2 className="font-heading font-bold">Confirmations de paiement</h2></div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Date</TableHead><TableHead>Facture</TableHead>
                <TableHead>N° confirmation</TableHead><TableHead className="text-right">Montant</TableHead><TableHead className="text-right">Reçu</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.payments || []).length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Aucune confirmation de paiement pour le moment.</TableCell></TableRow>
              )}
              {(data?.payments || []).map((p, i) => (
                <TableRow key={i} data-testid={`supplier-payment-${i}`}>
                  <TableCell className="text-sm text-muted-foreground">{dateFr(p.date)}</TableCell>
                  <TableCell className="text-sm">{p.description}</TableCell>
                  <TableCell className="font-mono text-sm">{p.confirmation_number}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{money(p.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`supplier-receipt-${i}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openReceipt(p.expense_id, p.confirmation_number)}><Receipt size={18} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Invoices */}
        <div className="border border-border bg-card rounded-sm overflow-x-auto">
          <div className="px-6 py-4 border-b border-border"><h2 className="font-heading font-bold">Mes factures</h2></div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Date</TableHead><TableHead>Description</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Payé</TableHead>
                <TableHead>Statut</TableHead><TableHead className="text-right">Écart</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.expenses || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Aucune facture.</TableCell></TableRow>
              )}
              {(data?.expenses || []).map((e) => (
                <TableRow key={e.id} data-testid={`supplier-invoice-${e.id}`}>
                  <TableCell className="text-sm text-muted-foreground">{dateFr(e.date)}</TableCell>
                  <TableCell className="text-sm">{e.description || e.vendor}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(e.total)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(e.amount_paid || 0)}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-sm border ${e.payment_status === "paid" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : e.payment_status === "partial" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                      {e.payment_status === "paid" ? "Payée" : e.payment_status === "partial" ? "Partiel" : "À payer"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {e.dispute ? (
                      <span className={`text-xs px-2 py-0.5 rounded-sm border ${e.dispute.status === "resolved" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                        {e.dispute.status === "resolved" ? "Écart résolu" : "Écart signalé"}
                      </span>
                    ) : (
                      <Button data-testid={`dispute-${e.id}`} variant="outline" size="sm" className="rounded-sm" onClick={() => { setDisputeFor(e); setDisputeMsg(""); }}>
                        <Warning size={14} className="mr-1" /> Signaler
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Dispute dialog */}
      <Dialog open={!!disputeFor} onOpenChange={(v) => !v && setDisputeFor(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Warning size={20} /> Signaler un écart</DialogTitle></DialogHeader>
          {disputeFor && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Facture : <span className="text-foreground font-medium">{disputeFor.description || disputeFor.vendor}</span> — {money(disputeFor.total)} ({dateFr(disputeFor.date)})
              </div>
              <Textarea data-testid="dispute-message" className="rounded-sm min-h-[120px]" placeholder="Décrivez l'écart constaté (montant, quantité, taxe, etc.)" value={disputeMsg} onChange={(e) => setDisputeMsg(e.target.value)} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDisputeFor(null)}>Annuler</Button>
            <Button data-testid="submit-dispute-btn" className="rounded-sm" onClick={submitDispute}>Envoyer le signalement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
